import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { BellIcon, CheckCircleIcon, CircleIcon, TrashIcon, SpeakerSimpleHighIcon, SpeakerSimpleSlashIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const SOUND_PREF_KEY = "bidora.notif_sound";

// Plays a tiny two-note chime via WebAudio. Self-contained — no asset
// ships with the bundle. We keep a single AudioContext alive (lazy-init
// on first user gesture) to satisfy mobile autoplay rules.
let _audioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  if (!_audioCtx) _audioCtx = new Ctor();
  if (_audioCtx.state === "suspended") {
    _audioCtx.resume().catch(() => {});
  }
  return _audioCtx;
}
function playBellChime() {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    // Two-note "bidora" chime: G5 → C6, ~120ms each, soft attack.
    const notes = [
      { freq: 783.99, start: 0, dur: 0.18 },
      { freq: 1046.5, start: 0.12, dur: 0.32 },
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    });
  } catch {
    /* WebAudio blocked or unavailable */
  }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      const v = localStorage.getItem(SOUND_PREF_KEY);
      return v === null ? true : v === "1";
    } catch {
      return true;
    }
  });
  const intervalRef = useRef(null);
  const prevUnreadRef = useRef(null); // null = uninitialized

  const refreshCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread((cur) => {
        const next = data.count;
        // Only chime when the count goes UP (i.e. a NEW notif arrived).
        // Skip the very first read so we don't ding on page load.
        if (
          prevUnreadRef.current !== null &&
          next > prevUnreadRef.current &&
          soundOn
        ) {
          playBellChime();
        }
        prevUnreadRef.current = next;
        return next;
      });
    } catch {
      /* network — keep prior count */
    }
  }, [user, soundOn]);

  const refreshList = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications", { params: { limit: 15 } });
      setItems(data.items);
    } catch {
      /* network */
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      setItems([]);
      prevUnreadRef.current = null;
      return;
    }
    refreshCount();
    intervalRef.current = setInterval(refreshCount, 20000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, refreshCount]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o) {
      // Touching the bell counts as a user gesture — perfect time to
      // unlock the AudioContext so future chimes work on mobile.
      getAudioCtx();
      await refreshList();
    }
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    prevUnreadRef.current = 0;
    setItems((prev) => prev.map((i) => ({ ...i, read: true })));
  };

  const clearAll = async () => {
    if (items.length === 0) return;
    try {
      await api.delete("/notifications");
      setItems([]);
      setUnread(0);
      prevUnreadRef.current = 0;
      toast.success("Notifications cleared.");
    } catch {
      toast.error("Couldn't clear notifications.");
    }
  };

  const toggleSound = () => {
    setSoundOn((cur) => {
      const next = !cur;
      try {
        localStorage.setItem(SOUND_PREF_KEY, next ? "1" : "0");
      } catch {
        /* localStorage blocked */
      }
      // Sample chime so users hear what they just turned on.
      if (next) playBellChime();
      return next;
    });
  };

  const markOneRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setUnread((u) => Math.max(0, u - 1));
    prevUnreadRef.current = Math.max(0, (prevUnreadRef.current || 0) - 1);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 hover:bg-zinc-100 transition-colors"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <BellIcon size={20} className="text-zinc-700" />
          {unread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#1D78AB] text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-count-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end" data-testid="notification-panel">
        <div className="p-3 border-b border-zinc-200 flex items-center justify-between gap-2">
          <div className="font-display font-bold">Notifications</div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleSound}
              className="h-7 w-7 grid place-items-center rounded-full hover:bg-zinc-100 text-zinc-500"
              aria-label={soundOn ? "Mute notification sound" : "Enable notification sound"}
              title={soundOn ? "Sound on — click to mute" : "Sound off — click to enable"}
              data-testid="notif-sound-toggle"
            >
              {soundOn ? (
                <SpeakerSimpleHighIcon size={15} />
              ) : (
                <SpeakerSimpleSlashIcon size={15} />
              )}
            </button>
            {items.some((i) => !i.read) && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#1D78AB] hover:underline px-2"
                data-testid="mark-all-read"
              >
                Mark all read
              </button>
            )}
            {items.length > 0 && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-1 text-xs text-rose-600 hover:underline px-2"
                aria-label="Clear all notifications"
                data-testid="clear-notifications-btn"
              >
                <TrashIcon size={13} />
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500">
              <BellIcon size={24} className="mx-auto mb-2 text-zinc-300" />
              No notifications yet.
            </div>
          ) : (
            items.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onMarkRead={() => markOneRead(n.id)}
                onNavigate={() => setOpen(false)}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationItem({ notification, onMarkRead, onNavigate }) {
  const { kind, title, body, link, read, created_at } = notification;
  const content = (
    <div
      className={`px-4 py-3 border-b border-zinc-100 flex items-start gap-3 hover:bg-zinc-50 transition-colors cursor-pointer ${
        !read ? "bg-blue-50/40" : ""
      }`}
      data-testid={`notification-${notification.id}`}
    >
      <div className="mt-0.5">
        {read ? (
          <CircleIcon size={10} className="text-zinc-300" />
        ) : (
          <CircleIcon size={10} weight="fill" className="text-[#1D78AB]" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold text-sm leading-tight line-clamp-1">{title}</p>
          <span className="text-[10px] text-zinc-500 flex-shrink-0 font-mono uppercase tracking-wider">
            {kindLabel(kind)}
          </span>
        </div>
        <p className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{body}</p>
        <p className="text-[10px] text-zinc-400 mt-1">
          {new Date(created_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
  if (link) {
    return (
      <Link
        to={link}
        onClick={() => {
          if (!read) onMarkRead();
          onNavigate?.();
        }}
      >
        {content}
      </Link>
    );
  }
  return (
    <div onClick={() => !read && onMarkRead()}>{content}</div>
  );
}

function kindLabel(k) {
  const map = {
    outbid: "BID",
    order_paid: "ORDER",
    order_sold: "SALE",
    order_shipped: "SHIP",
    order_delivered: "DONE",
    digital_unlocked: "UNLOCK",
    payout_released: "PAYOUT",
    verification_approved: "VERIFIED",
    verification_rejected: "ACTION",
    instant_payout_unlocked: "UNLOCK",
  };
  return map[k] || "NEW";
}
